
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { NavigationEnd, NavigationStart } from '@angular/router';

/**
 * Single definition of "the page scroller".
 *
 * The shell no longer scrolls the document: the header and footer are fixed to
 * the viewport and the region between them (`.app-scroll`) is the scroll
 * container, so the scrollbar stops running past the header and footer and both
 * span the full viewport width. Everything that used `window.scrollTo` /
 * `window.scrollY` / `window.pageYOffset` goes through here instead — on the
 * document those now read a permanently-zero scroll position.
 */
@Injectable({ providedIn: 'root' })
export class AppScrollService {
    /**
     * Kept in sync with the wrapper in app.component.html, `.app-scroll` in styles.css and the
     * `infiniteScrollContainer=".app-scroll"` inputs on the page lists (templates can't read this).
     */
    static readonly SCROLLER_SELECTOR = '.app-scroll';

    /** How long a Back/Forward restore keeps trying while the page fills in its data. */
    private static readonly RESTORE_WINDOW_MS = 1500;

    /** Scroll offsets of the pages in this tab's history, keyed by their history entry. */
    private readonly savedOffsets = new Map<number, number>();
    /** The history entry on screen: the navigation id Angular stored in its history state. */
    private shownEntry = 0;
    /** Set by a Back/Forward navigation for the NavigationEnd that follows it. */
    private pending: { entry: number; offset: number | null } | null = null;
    /** Bumped by every navigation, so a restore still running for an older one stops. */
    private restoreRun = 0;

    constructor(@Inject(DOCUMENT) private document: Document) { }

    /**
     * Router NavigationStart: remembers where the page being left was scrolled to.
     *
     * The router's own scroll restoration only ever moves the document, which no longer
     * scrolls, so the shell keeps the offsets itself.
     */
    navigationStarted(event: NavigationStart): void {
        this.restoreRun++;
        this.savedOffsets.set(this.shownEntry, this.getScrollTop());
        const entry = event.navigationTrigger === 'popstate' ? event.restoredState?.navigationId : undefined;
        this.pending = entry === undefined ? null : { entry, offset: this.savedOffsets.get(entry) ?? null };
    }

    /**
     * Router NavigationEnd: Back/Forward returns to where that page was left, anything else
     * opens at the top.
     */
    navigationEnded(event: NavigationEnd): void {
        const pending = this.pending;
        this.pending = null;
        // A Back/Forward lands on an existing history entry, which keeps its original id.
        this.shownEntry = pending?.entry ?? event.id;

        if (pending?.offset) {
            this.restoreOffset(pending.offset);
        } else {
            this.scrollToTop();
        }
    }

    /**
     * Scrolls back to `offset`. The page is rebuilt and fills in as its data arrives, so it may
     * not be tall enough yet: keep trying each frame until it is, for a short while, and stop
     * as soon as the user scrolls themselves.
     */
    private restoreOffset(offset: number): void {
        const run = ++this.restoreRun;
        const until = Date.now() + AppScrollService.RESTORE_WINDOW_MS;
        const scroller = this.getScroller();
        const userScrolled = () => this.restoreRun++;
        const done = () => {
            scroller?.removeEventListener('wheel', userScrolled);
            scroller?.removeEventListener('touchstart', userScrolled);
        };
        scroller?.addEventListener('wheel', userScrolled, { passive: true });
        scroller?.addEventListener('touchstart', userScrolled, { passive: true });

        const attempt = () => {
            if (run !== this.restoreRun) return done();
            this.scrollTo({ top: offset });
            if (Math.abs(this.getScrollTop() - offset) > 1 && Date.now() < until) {
                requestAnimationFrame(attempt);
            } else {
                done();
            }
        };
        requestAnimationFrame(attempt);
    }

    /** The scrolling element, or null before the shell has rendered. */
    getScroller(): HTMLElement | null {
        return this.document.querySelector<HTMLElement>(AppScrollService.SCROLLER_SELECTOR);
    }

    /** Current scroll offset, 0 when the shell has not rendered yet. */
    getScrollTop(): number {
        return this.getScroller()?.scrollTop ?? 0;
    }

    scrollTo(options: ScrollToOptions): void {
        this.getScroller()?.scrollTo(options);
    }

    scrollToTop(behavior: ScrollBehavior = 'auto'): void {
        this.scrollTo({ top: 0, behavior });
    }
}
